import React, { useState, useEffect } from 'react';

const LegalTextEditor = () => {
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlug, setEditingSlug] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => { loadTexts(); }, []);

  const loadTexts = async () => {
    try {
      const res = await fetch('/api/v1/legal_texts', {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) setTexts(data.legal_texts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (text) => {
    setEditingSlug(text.slug);
    setEditForm({ title: text.title, content: text.content });
  };

  const cancelEdit = () => {
    setEditingSlug(null);
    setEditForm({ title: '', content: '' });
  };

  const handleSave = async (slug) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/v1/legal_texts/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ legal_text: editForm })
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || 'Texte mis à jour');
        setEditingSlug(null);
        loadTexts();
      } else {
        showToast(data.message || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setSaving(false);
    }
  };

  const colors = {
    primary: '#7c2837', primaryDark: '#5a1d28', card: '#ffffff', text: '#2c0e16',
    textMuted: '#6c4a53', border: '#e8d0d6', success: '#28a745', danger: '#dc3545',
  };

  const cardStyle = {
    background: colors.card, borderRadius: 12, padding: 25,
    boxShadow: '0 2px 12px rgba(124,40,55,0.08)', border: `1px solid ${colors.border}`, marginBottom: 20,
  };

  const btnStyle = (variant = 'primary') => {
    const map = {
      primary: { bg: colors.primary, color: '#fff' },
      success: { bg: colors.success, color: '#fff' },
      outline: { bg: 'transparent', color: colors.primary, border: `1px solid ${colors.primary}` },
      danger: { bg: colors.danger, color: '#fff' },
    };
    const v = map[variant] || map.primary;
    return {
      padding: '8px 18px', borderRadius: 8, border: v.border || 'none',
      background: v.bg, color: v.color, cursor: 'pointer', fontSize: 14, fontWeight: 600,
    };
  };

  const toastStyle = {
    position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '14px 24px', borderRadius: 10,
    color: '#fff', fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    background: toast?.type === 'error' ? colors.danger : colors.success,
  };

  const slugIcons = { 'droit-image': '📷', 'reglement-interieur': '📜' };
  const slugUrls = { 'droit-image': '/mentions-legales/droit-image', 'reglement-interieur': '/mentions-legales/reglement-interieur' };

  if (loading) return <p style={{ color: colors.textMuted }}>⏳ Chargement des textes légaux...</p>;

  return (
    <div>
      {toast && <div style={toastStyle}>{toast.message}</div>}

      {texts.map(text => (
        <div key={text.slug} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <h4 style={{ margin: 0, color: colors.primary }}>
                {slugIcons[text.slug] || '📄'} {text.title}
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textMuted }}>
                Dernière modification : {new Date(text.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={slugUrls[text.slug]} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle('outline'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                👁️ Voir
              </a>
              {editingSlug !== text.slug && (
                <button onClick={() => startEdit(text)} style={btnStyle('primary')}>
                  ✏️ Modifier
                </button>
              )}
            </div>
          </div>

          {editingSlug === text.slug ? (
            <div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: colors.textMuted }}>Titre</label>
                <input
                  type="text" value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: colors.textMuted }}>
                  Contenu (HTML autorisé)
                </label>
                <textarea
                  value={editForm.content}
                  onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13, fontFamily: 'monospace', minHeight: 300, boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: 16, padding: 16, background: '#f8f0f2', borderRadius: 8, border: `1px solid ${colors.border}` }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13, color: colors.primary }}>📋 Aperçu</label>
                <div style={{ lineHeight: 1.6, fontSize: 14 }} dangerouslySetInnerHTML={{ __html: editForm.content }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => handleSave(text.slug)} disabled={saving} style={btnStyle('success')}>
                  {saving ? '⏳ Sauvegarde...' : '✅ Enregistrer'}
                </button>
                <button onClick={cancelEdit} style={btnStyle('outline')}>Annuler</button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 16, background: '#fdf8f9', borderRadius: 8, maxHeight: 200, overflow: 'auto', fontSize: 13, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: text.content }} />
          )}
        </div>
      ))}
    </div>
  );
};

export default LegalTextEditor;
